import { Component, computed, input, Optional, SkipSelf } from '@angular/core';

/**
 * services
 */
import { TableStateService } from '../../services';

/**
 * @component TableSortIconComponent
 * @description Displays a sort icon based on the current sort order for a column.
 *
 * Host classes:
 * - sorted-asc / sorted-desc: applied based on current sort direction
 * - bg-dark / text-white: applied when column is currently sorted
 */
@Component({
  selector: 'app-table-sort-icon',
  templateUrl: './table-sort-icon.component.html',
  styleUrl: './table-sort-icon.component.scss',
  imports: []
})
export class TableSortIconComponent {

  /**
   * Field name to resolve sort order for.
   */
  public field = input.required<string>();

  /**
   * Icon class when field is not sorted.
   */
  public defaultIcon = input<string>('fa-solid fa-arrow-up-arrow-down');

  /**
   * Icon class when field is sorted ascending.
   */
  public ascIcon = input<string>('fa-regular fa-arrow-up-short-wide');

  /**
   * Icon class when field is sorted descending.
   */
  public descIcon = input<string>('fa-regular fa-arrow-down-wide-short');

  /**
   * Current order for this field.
   */
  public readonly sortOrder = computed(() => {
    if (!this.tableState) return null;
    return this.tableState.getSortOrder(this.field());
  });

  /**
   * Resolved icon class based on current sort order.
   */
  public readonly iconClass = computed(() => {
    const order = this.sortOrder();

    if (order === 'asc') return this.ascIcon();
    if (order === 'desc') return this.descIcon();

    return this.defaultIcon();
  });

  /**
   * `@SkipSelf()` rather than `@Host()` — see the note on
   * `TableSortableColumnDirective`. Under `@Host()` this resolved only for a
   * sort icon written in a consumer's `theadTemplate`; in the table's own
   * default `thead` it fell to `null` and the icon stayed permanently
   * unsorted.
   */
  constructor(
    @Optional()
    @SkipSelf()
    public readonly tableState: TableStateService
  ) {}
}
